import type {
  BossRevolveSceneContour,
  BossRevolveSessionContourSnapshot,
  ProfileRegionGeometry,
} from '@puppycad/cad-core';

/**
 * 构建旋转创建态轮廓快照。
 *
 * 设计说明：
 * 1. web-client 只能保存用户选择态，不应该把 THREE.Object3D 传入 cad-core 命令链路；
 * 2. 闭合区域选择依赖临时高亮 mesh，后续创建命令必须使用纯几何数据才能稳定建模；
 * 3. 普通草图边轮廓与闭合区域最终都收口成同一份快照，保证两种选择行为等价。
 *
 * @param contours 当前已选的旋转轮廓。
 * @returns 可被 cad-core SessionBridge 消费的纯数据快照。
 */
export function buildBossRevolveSessionContourSnapshots(
  contours: BossRevolveSceneContour[],
): BossRevolveSessionContourSnapshot[] {
  return contours.map((contour) => ({
    id: contour.id,
    sketchId: contour.sketchId,
    sourceObjectId: contour.sourceObjectId,
    contourKey: contour.contourKey,
    geometry: cloneProfileRegionGeometry(contour.geometry),
    regionIdentity: contour.regionIdentity
      ? structuredClone(contour.regionIdentity)
      : undefined,
  }));
}

/**
 * 克隆轮廓区域几何。
 *
 * @param geometry 原始区域几何。
 * @returns 与场景轮廓解耦的区域几何。
 */
function cloneProfileRegionGeometry(geometry: ProfileRegionGeometry): ProfileRegionGeometry {
  return {
    outer: geometry.outer.map((point) => clonePointTuple(point)),
    outerSegments: geometry.outerSegments
      ? structuredClone(geometry.outerSegments)
      : undefined,
    holes: geometry.holes?.map((hole) => hole.map((point) => clonePointTuple(point))),
    holeSegments: geometry.holeSegments
      ? structuredClone(geometry.holeSegments)
      : undefined,
    normal: clonePointTuple(geometry.normal),
    xAxis: clonePointTuple(geometry.xAxis),
    yAxis: clonePointTuple(geometry.yAxis),
  };
}

/**
 * 克隆三维点元组。
 *
 * @param point 三维点或方向元组。
 * @returns 新的三维元组。
 */
function clonePointTuple(point: [number, number, number]): [number, number, number] {
  return [point[0], point[1], point[2]];
}
